import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Coins, Plus, Upload, CheckCircle2, XCircle, ImageIcon, Grid3x3 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const IUR_STATUS = {
  LUNAS: { label: "LUNAS", tone: "success" },
  SEBAGIAN: { label: "SEBAGIAN", tone: "warning" },
  BELUM_BAYAR: { label: "BELUM BAYAR", tone: "muted" },
  TERLAMBAT: { label: "TERLAMBAT", tone: "destructive" },
  MENUNGGU_VERIFIKASI: { label: "MENUNGGU VERIFIKASI", tone: "info" },
  VERIFIED: { label: "VERIFIED", tone: "success" },
  REJECTED: { label: "DITOLAK", tone: "destructive" },
};

function formatIDR(n) {
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);
}

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Iuran() {
  const { user } = useAuth();
  const [periods, setPeriods] = useState([]);
  const [activePeriod, setActivePeriod] = useState("");
  const [summary, setSummary] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [tab, setTab] = useState("period");
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", date_paid: new Date().toISOString().slice(0,10), notes: "", evidence_base64: "", evidence_name: "" });
  const [newPeriodOpen, setNewPeriodOpen] = useState(false);
  const [newPeriod, setNewPeriod] = useState({ period: "", label: "", amount_per_church: "500000", due_date: "" });
  const [evidenceView, setEvidenceView] = useState(null);

  const canManage = ["SUPER_ADMIN","ADMIN_KLASIS","BENDAHARA_KLASIS"].includes(user?.role);
  const canPay = canManage || ["BENDAHARA_JEMAAT","SEKRETARIS_JEMAAT","GEMBALA_JEMAAT"].includes(user?.role);

  const loadPeriods = async () => {
    const { data } = await api.get("/iuran/periods");
    setPeriods(data);
    if (data.length && !activePeriod) setActivePeriod(data[0].period);
    return data;
  };

  const loadSummary = async (period) => {
    if (!period) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/iuran/summary/${period}`);
      setSummary(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const loadMatrix = async () => {
    try {
      const { data } = await api.get("/iuran/matrix");
      setMatrix(data);
    } catch (e) { /* silent — some roles cannot view */ }
  };

  useEffect(() => { loadPeriods(); }, []);
  useEffect(() => { if (activePeriod) loadSummary(activePeriod); }, [activePeriod]);
  useEffect(() => { if (tab === "matrix" && !matrix) loadMatrix(); }, [tab, matrix]);

  const openPay = (row) => {
    setSelectedRow(row);
    const remaining = Math.max(0, (summary.period.amount_per_church || 0) - (row.total_paid || 0));
    setPayForm({ amount: remaining || "", date_paid: new Date().toISOString().slice(0,10), notes: "", evidence_base64: "", evidence_name: "" });
    setPayOpen(true);
  };

  const submitPay = async () => {
    if (!selectedRow) return;
    if (Number(payForm.amount) <= 0) { toast.error("Jumlah harus > 0"); return; }
    try {
      await api.post("/iuran/payments", {
        period: activePeriod,
        church_id: selectedRow.church.id,
        amount: Number(payForm.amount),
        date_paid: payForm.date_paid,
        notes: payForm.notes || null,
        evidence_base64: payForm.evidence_base64 || null,
        evidence_name: payForm.evidence_name || null,
      });
      toast.success("Pembayaran berhasil dicatat.");
      setPayOpen(false); setSelectedRow(null);
      loadSummary(activePeriod);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const verifyPayment = async (paymentId, status) => {
    try {
      await api.patch(`/iuran/payments/${paymentId}/verify`, { status });
      toast.success(status === "VERIFIED" ? "Pembayaran diverifikasi." : "Pembayaran ditolak.");
      loadSummary(activePeriod);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const viewEvidence = async (paymentId) => {
    try {
      const { data } = await api.get(`/iuran/payments/${paymentId}/evidence`);
      if (!data.evidence_base64) { toast.info("Belum ada bukti diupload."); return; }
      setEvidenceView(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createPeriod = async () => {
    if (!newPeriod.period || !newPeriod.label || !newPeriod.due_date) { toast.error("Semua field wajib diisi."); return; }
    try {
      await api.post("/iuran/periods", {
        period: newPeriod.period, label: newPeriod.label,
        amount_per_church: Number(newPeriod.amount_per_church),
        due_date: newPeriod.due_date, notes: null,
      });
      toast.success("Periode iuran dibuat.");
      setNewPeriodOpen(false);
      const list = await loadPeriods();
      setActivePeriod(newPeriod.period);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rows = summary?.rows || [];
  const target = summary?.total_target || 0;
  const paid = summary?.total_paid || 0;
  const pct = target > 0 ? Math.round((paid / target) * 100) : 0;

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Keuangan</div>
          <h1 className="font-heading font-bold text-3xl mt-1">Iuran Jemaat</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">Kewajiban iuran per jemaat dengan target vs realisasi, tunggakan, dan upload bukti pembayaran.</p>
        </div>
        <div className="flex items-center gap-2">
          {periods.length > 0 && (
            <Select value={activePeriod} onValueChange={setActivePeriod}>
              <SelectTrigger className="w-56" data-testid="select-iuran-period"><SelectValue placeholder="Pilih periode" /></SelectTrigger>
              <SelectContent>
                {periods.map((p) => <SelectItem key={p.period} value={p.period}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {canManage && (
            <Dialog open={newPeriodOpen} onOpenChange={setNewPeriodOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="btn-new-iuran-period"><Plus className="h-4 w-4 mr-2" /> Periode</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Periode Iuran Baru</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Kode periode</Label><Input value={newPeriod.period} onChange={(e)=>setNewPeriod({...newPeriod, period:e.target.value})} placeholder="2026-Q2" data-testid="input-period-code" /></div>
                  <div><Label>Label</Label><Input value={newPeriod.label} onChange={(e)=>setNewPeriod({...newPeriod, label:e.target.value})} placeholder="Iuran Triwulan II 2026" data-testid="input-period-label" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Iuran per jemaat</Label><Input type="number" value={newPeriod.amount_per_church} onChange={(e)=>setNewPeriod({...newPeriod, amount_per_church:e.target.value})} data-testid="input-period-amount" /></div>
                    <div><Label>Jatuh tempo</Label><Input type="date" value={newPeriod.due_date} onChange={(e)=>setNewPeriod({...newPeriod, due_date:e.target.value})} data-testid="input-period-due" /></div>
                  </div>
                </div>
                <DialogFooter><Button onClick={createPeriod} data-testid="btn-save-period">Simpan</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="period" data-testid="tab-iuran-period">Periode Aktif</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="tab-iuran-matrix"><Grid3x3 className="h-3.5 w-3.5 mr-1.5" /> Rekap Lintas Periode</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "matrix" ? (
        !matrix ? (
          <div className="text-sm text-muted-foreground">Memuat matrix…</div>
        ) : matrix.periods.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg p-12 text-center">
            <Coins className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <div className="text-sm text-muted-foreground">Belum ada periode iuran.</div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card><CardContent className="p-5">
                <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Total Periode</div>
                <div className="font-heading font-bold text-2xl mt-1">{matrix.periods.length}</div>
                <div className="text-xs text-muted-foreground mt-1">{matrix.churches_count} jemaat</div>
              </CardContent></Card>
              <Card><CardContent className="p-5">
                <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Target Gabungan</div>
                <div className="font-heading font-bold text-2xl mt-1">{formatIDR(matrix.per_period_totals.reduce((s, p) => s + p.target, 0))}</div>
                <div className="text-xs text-muted-foreground mt-1">Semua periode × jemaat</div>
              </CardContent></Card>
              <Card><CardContent className="p-5">
                <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Terkumpul (Verified)</div>
                <div className="font-heading font-bold text-2xl mt-1 text-primary">{formatIDR(matrix.per_period_totals.reduce((s, p) => s + p.paid, 0))}</div>
              </CardContent></Card>
            </div>

            <Card>
              <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Matrix Kelunasan — Jemaat × Periode</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 sticky left-0 bg-muted/50">Jemaat</th>
                        {matrix.periods.map((p) => (
                          <th key={p.period} className="text-center p-2 whitespace-nowrap">{p.label}</th>
                        ))}
                        <th className="text-center p-2">Kepatuhan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matrix.matrix.map((row) => (
                        <tr key={row.church.id} className="border-t border-border" data-testid={`row-matrix-${row.church.code}`}>
                          <td className="p-2 font-medium sticky left-0 bg-background">{row.church.short_name || row.church.name}</td>
                          {row.cells.map((c) => (
                            <td key={c.period} className="p-2 text-center" data-testid={`cell-${row.church.code}-${c.period}`}>
                              <StatusBadge
                                status={c.status === "LUNAS" ? "success" : c.status === "SEBAGIAN" ? "warning" : c.status === "TERLAMBAT" ? "destructive" : "muted"}
                                label={
                                  c.status === "LUNAS" ? "✓" :
                                  c.status === "SEBAGIAN" ? `${Math.round(c.paid/c.target*100)}%` :
                                  c.status === "TERLAMBAT" ? "!" : "—"
                                }
                              />
                            </td>
                          ))}
                          <td className="p-2 text-center">
                            <div className="flex flex-col items-center gap-0.5">
                              <StatusBadge status={row.compliance >= 100 ? "success" : row.compliance >= 50 ? "info" : row.compliance > 0 ? "warning" : "destructive"} label={`${row.compliance}%`} />
                              <span className="text-[0.6rem] text-muted-foreground">{row.lunas_count}/{row.period_count}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t-2 border-primary/30 bg-muted/30">
                      <tr>
                        <td className="p-2 font-semibold">Total Terkumpul</td>
                        {matrix.per_period_totals.map((p) => (
                          <td key={p.period} className="p-2 text-center whitespace-nowrap">
                            <div className="font-mono text-[0.7rem]">{formatIDR(p.paid)}</div>
                            <div className="text-[0.6rem] text-muted-foreground">{p.lunas_count}/{p.total_churches} lunas</div>
                          </td>
                        ))}
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )
      ) : loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : !summary ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center">
          <Coins className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Belum ada periode iuran.</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card><CardContent className="p-5">
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Target Total</div>
              <div className="font-heading font-bold text-2xl mt-1">{formatIDR(target)}</div>
              <div className="text-xs text-muted-foreground mt-1">{formatIDR(summary.period.amount_per_church)} × {summary.total_churches} jemaat</div>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Realisasi</div>
              <div className="font-heading font-bold text-2xl mt-1 text-primary">{formatIDR(paid)}</div>
              <Progress value={pct} className="h-1.5 mt-2" />
              <div className="text-xs text-muted-foreground mt-1">{pct}% dari target</div>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Tunggakan</div>
              <div className="font-heading font-bold text-2xl mt-1 text-secondary">{formatIDR(summary.outstanding)}</div>
              <div className="text-xs text-muted-foreground mt-1">Belum dibayar</div>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Jemaat Lunas</div>
              <div className="font-heading font-bold text-2xl mt-1">{summary.lunas} <span className="text-lg text-muted-foreground">/ {summary.total_churches}</span></div>
              <div className="text-xs text-muted-foreground mt-1">Jatuh tempo: {summary.period.due_date}</div>
            </CardContent></Card>
          </div>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Rincian per Jemaat</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Jemaat</TableHead>
                    <TableHead className="text-right">Kewajiban</TableHead>
                    <TableHead className="text-right">Dibayar</TableHead>
                    <TableHead className="text-right">Sisa</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Pembayaran</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const remaining = Math.max(0, (summary.period.amount_per_church || 0) - r.total_paid);
                    const canPayThis = canPay && (canManage || user.church_id === r.church.id);
                    const meta = IUR_STATUS[r.status] || IUR_STATUS.BELUM_BAYAR;
                    return (
                      <TableRow key={r.church.id} data-testid={`iuran-row-${r.church.code}`}>
                        <TableCell>
                          <div className="font-medium text-sm">{r.church.name}</div>
                          <div className="text-xs text-muted-foreground">{r.church.code}</div>
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">{formatIDR(summary.period.amount_per_church)}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-primary">{formatIDR(r.total_paid)}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-secondary">{formatIDR(remaining)}</TableCell>
                        <TableCell><span className={`status-badge bg-${meta.tone==='success'?'emerald':meta.tone==='warning'?'amber':meta.tone==='destructive'?'rose':meta.tone==='info'?'sky':'stone'}-100 text-${meta.tone==='success'?'emerald':meta.tone==='warning'?'amber':meta.tone==='destructive'?'rose':meta.tone==='info'?'sky':'stone'}-800 border border-${meta.tone==='success'?'emerald':meta.tone==='warning'?'amber':meta.tone==='destructive'?'rose':meta.tone==='info'?'sky':'stone'}-200`}>{meta.label}</span></TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {r.payments.map((pm) => (
                              <div key={pm.id} className="flex items-center gap-2 text-xs">
                                <span className="font-mono">{formatIDR(pm.amount)}</span>
                                <StatusBadge status={pm.status} label={IUR_STATUS[pm.status]?.label || pm.status} />
                                {pm.evidence_base64 !== undefined && (
                                  <button onClick={()=>viewEvidence(pm.id)} className="text-primary hover:underline text-[0.7rem]" data-testid={`btn-view-evidence-${pm.id}`}>
                                    <ImageIcon className="h-3 w-3 inline" /> bukti
                                  </button>
                                )}
                                {canManage && pm.status === "MENUNGGU_VERIFIKASI" && (
                                  <>
                                    <button onClick={()=>verifyPayment(pm.id, "VERIFIED")} className="text-emerald-700 hover:underline text-[0.7rem]" data-testid={`btn-verify-payment-${pm.id}`}><CheckCircle2 className="h-3 w-3 inline" /></button>
                                    <button onClick={()=>verifyPayment(pm.id, "REJECTED")} className="text-rose-700 hover:underline text-[0.7rem]"><XCircle className="h-3 w-3 inline" /></button>
                                  </>
                                )}
                              </div>
                            ))}
                            {r.payments.length === 0 && <span className="text-xs text-muted-foreground italic">—</span>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {canPayThis && r.status !== "LUNAS" && (
                            <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={()=>openPay(r)} data-testid={`btn-pay-${r.church.code}`}>
                              Bayar
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Catat Pembayaran Iuran</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              {selectedRow?.church?.name} • {summary?.period?.label}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Jumlah (Rp)</Label><Input type="number" value={payForm.amount} onChange={(e)=>setPayForm({...payForm, amount:e.target.value})} data-testid="input-pay-amount" /></div>
              <div><Label>Tanggal bayar</Label><Input type="date" value={payForm.date_paid} onChange={(e)=>setPayForm({...payForm, date_paid:e.target.value})} data-testid="input-pay-date" /></div>
            </div>
            <div>
              <Label>Upload bukti (opsional, JPG/PNG max 1.5MB)</Label>
              <Input type="file" accept="image/*" data-testid="input-pay-evidence"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  if (f.size > 1_500_000) { toast.error("File terlalu besar (maks 1.5MB)"); return; }
                  const b64 = await fileToBase64(f);
                  setPayForm({...payForm, evidence_base64: b64, evidence_name: f.name});
                }}
              />
              {payForm.evidence_name && <div className="text-xs text-muted-foreground mt-1">✓ {payForm.evidence_name}</div>}
            </div>
            <div><Label>Catatan</Label><Textarea rows={2} value={payForm.notes} onChange={(e)=>setPayForm({...payForm, notes:e.target.value})} /></div>
          </div>
          <DialogFooter><Button onClick={submitPay} data-testid="btn-save-payment"><Upload className="h-4 w-4 mr-2"/>Simpan Pembayaran</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Evidence view */}
      <Dialog open={!!evidenceView} onOpenChange={(v) => !v && setEvidenceView(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Bukti Pembayaran</DialogTitle></DialogHeader>
          {evidenceView?.evidence_base64 && (
            <img src={evidenceView.evidence_base64} alt={evidenceView.evidence_name || "bukti"} className="w-full rounded-md border border-border" />
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
